/**
 * Pure helpers for the ReleaseNetworkDialog (step 2.2).
 *
 * Extracted from the JSX so the mode / validation / display logic can be
 * regression-locked at the unit level. The dialog itself is a thin shell
 * around these helpers.
 *
 * Two release modes:
 *   - NEW_POOL: creates a brand-new customer pool at the next version.
 *   - PATCH_EXISTING_POOLS: advances the release pointer on selected pools.
 *
 * Design invariants (step 2 decision log):
 *   - "First release ever" UX never shows the patch tab (there are no pools
 *     to patch).
 *   - Patch mode requires ≥1 selected pool.
 *   - Mode selection is per-pool (not all-or-nothing) → the picker is a
 *     multi-select checkbox list.
 *   - Release is initiated from this dialog only; the banner elsewhere is
 *     release-initiated, never "pool behind" drift.
 *
 * Phase A additions (governance fields, NEW_POOL only):
 *   - ``ownerEmail``        — pool CREATOR (defaults to the releaser).
 *   - ``supervisorEmails``  — extra workspace SUPERVISORs to seed onto the
 *                              pool roster at release time.
 *   - ``trigger``           — HEADLESS / HYBRID / MANUAL (see networkTrigger.js).
 *
 * These fields ride along on the same NEW_POOL payload — the backend's
 * ``release_network_handler`` ignores unknown keys today (Phase A) and will
 * start honouring them in Phase B. Patch mode never carries them: roster +
 * trigger live on the pool row, not on the network release.
 */

import {
  DEFAULT_TRIGGER_MODE,
  normalizeTriggerMode,
} from './networkTrigger.js';

export const RELEASE_MODE_NEW_POOL = 'NEW_POOL';
export const RELEASE_MODE_PATCH_EXISTING_POOLS = 'PATCH_EXISTING_POOLS';

/**
 * Is this the network's first release ever?
 *
 * A network is "pre-first-release" when it has zero pools attached. We don't
 * rely on ``releaseVersion`` because a failed release could leave a stale
 * version without any pool — treating pool count as the ground truth keeps
 * the UX consistent with what the user can actually act on.
 *
 * @param {Array<unknown> | null | undefined} pools
 * @returns {boolean}
 */
export function isFirstReleaseEver(pools) {
  if (!Array.isArray(pools)) return true;
  return pools.length === 0;
}

/**
 * Compute which release modes are valid to offer the user.
 *
 * @param {Array<unknown> | null | undefined} pools
 * @returns {{ canPatch: boolean, canCreateNewPool: boolean }}
 */
export function availableReleaseModes(pools) {
  const hasPools = Array.isArray(pools) && pools.length > 0;
  return {
    canPatch: hasPools,
    canCreateNewPool: true, // Always allowed — parallel pools are a 1st-class use case.
  };
}

/**
 * Given the currently selected mode and the list of pools, decide the
 * default mode to land on when the dialog opens. Falls back to NEW_POOL
 * when patching isn't available yet (first release).
 *
 * @param {Array<unknown> | null | undefined} pools
 * @param {string | null | undefined} preferredMode
 * @returns {string}
 */
export function pickDefaultReleaseMode(pools, preferredMode) {
  const modes = availableReleaseModes(pools);
  const normalized = typeof preferredMode === 'string' ? preferredMode.trim().toUpperCase() : '';
  if (normalized === RELEASE_MODE_PATCH_EXISTING_POOLS && modes.canPatch) {
    return RELEASE_MODE_PATCH_EXISTING_POOLS;
  }
  // Always default to NEW_POOL ("Release to a new pool") on dialog open —
  // even when patchable pools exist. The user can flip to PATCH explicitly
  // if that's what they want; auto-flipping was confusing because the
  // dialog title says "Create a Room" yet the radio jumped to patch.
  return RELEASE_MODE_NEW_POOL;
}

/**
 * Normalise an API pool row into the shape the dialog renders. Tolerates
 * minor shape drift (``releaseVersion`` missing, ``status`` lowercase, etc).
 *
 * @param {{ poolId?: string, name?: string, releaseVersion?: number | null, status?: string | null, lastUpgradedAt?: string | null } | null | undefined} p
 * @returns {{ poolId: string, name: string, releaseVersion: number, status: string, lastUpgradedAt: string | null } | null}
 */
export function normalizePool(p) {
  if (!p || typeof p !== 'object') return null;
  const poolId = typeof p.poolId === 'string' ? p.poolId.trim() : '';
  if (!poolId) return null;
  return {
    poolId,
    name: typeof p.name === 'string' && p.name ? p.name : poolId,
    releaseVersion: Number.isFinite(p.releaseVersion) ? Number(p.releaseVersion) : 0,
    status: String(p.status || 'ACTIVE').toUpperCase(),
    lastUpgradedAt: typeof p.lastUpgradedAt === 'string' ? p.lastUpgradedAt : null,
  };
}

/**
 * Normalise a pool list and filter out ones that are not safely patchable.
 *
 * We skip ``DELETED`` / ``ORPHANED`` pools — those can't receive a patch and
 * would produce a confusing 409. The backend also refuses, so this is a UX
 * guard, not a security boundary.
 *
 * @param {Array<unknown> | null | undefined} pools
 * @param {number | null | undefined} nextReleaseVersion
 * @returns {Array<{ poolId: string, name: string, releaseVersion: number, status: string, isBehind: boolean, lastUpgradedAt: string | null }>}
 */
export function patchablePools(pools, nextReleaseVersion) {
  if (!Array.isArray(pools)) return [];
  const target = Number.isFinite(nextReleaseVersion) ? Number(nextReleaseVersion) : null;
  const out = [];
  for (const p of pools) {
    const n = normalizePool(p);
    if (!n) continue;
    if (n.status === 'DELETED' || n.status === 'ORPHANED') continue;
    const isBehind = target == null ? true : n.releaseVersion < target;
    out.push({ ...n, isBehind });
  }
  return out;
}

/**
 * Compute whether the dialog's Submit button should be enabled.
 *
 * @param {{ mode: string, poolName?: string, selectedPoolIds?: string[], availablePools?: Array<unknown> | null }} state
 * @returns {{ enabled: boolean, reason: string | null }}
 */
export function canSubmitRelease(state) {
  const mode = (state?.mode || '').toUpperCase();
  if (mode === RELEASE_MODE_PATCH_EXISTING_POOLS) {
    const selected = Array.isArray(state?.selectedPoolIds) ? state.selectedPoolIds.filter(Boolean) : [];
    if (selected.length === 0) {
      return { enabled: false, reason: 'Select at least one pool to patch.' };
    }
    const pools = state?.availablePools;
    if (Array.isArray(pools) && pools.length === 0) {
      return { enabled: false, reason: 'No existing pools to patch.' };
    }
    // Upgrade-to-existing-version path (the MAJOR-upgrade case): the network
    // has no NEW changes to cut, but a selected room is behind an
    // already-released version (e.g. room on 1.00 while 2.00 exists). We
    // advance it to that EXISTING version — no new cut — so the isNoop /
    // "nothing new to release" gates below DON'T apply. A real version gap
    // is being closed. The dialog only sets this flag when ≥1 selected pool
    // is genuinely behind the target existing version.
    if (state?.isUpgradeToExisting) {
      return { enabled: true, reason: null };
    }
    if (state?.isNoop) {
      return {
        enabled: false,
        reason: 'These rooms are already on the latest version. Make a change to the network first to create a new version.',
      };
    }
    if (state?.hasPendingReleaseChanges === false) {
      return { enabled: false, reason: 'No new updates to create a version.' };
    }
    return { enabled: true, reason: null };
  }
  if (mode === RELEASE_MODE_NEW_POOL) {
    const poolName = (state?.poolName || '').trim();
    if (!poolName) return { enabled: false, reason: 'Enter a pool name.' };
    return { enabled: true, reason: null };
  }
  return { enabled: false, reason: 'Select a release mode.' };
}

/**
 * Lower-cased, deduped email list, with optional removals (e.g. drop the
 * pool owner so they aren't duplicated in supervisorEmails). Pure helper —
 * extracted so the dialog and the test harness compute identical lists.
 *
 * @param {Array<unknown> | null | undefined} emails
 * @param {Array<unknown> | null | undefined} excludeEmails
 * @returns {string[]}
 */
export function normalizeSupervisorEmails(emails, excludeEmails) {
  if (!Array.isArray(emails)) return [];
  const exclude = new Set(
    (Array.isArray(excludeEmails) ? excludeEmails : [])
      .map((e) => (typeof e === 'string' ? e.trim().toLowerCase() : ''))
      .filter(Boolean),
  );
  const seen = new Set();
  const out = [];
  for (const raw of emails) {
    const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (!v || seen.has(v) || exclude.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

/**
 * Compose the final submit payload for the API helper. Kept separate from
 * ``buildReleaseRequestBody`` in the API layer — this one accepts the raw
 * dialog state and produces the same object shape, so tests can verify the
 * full UI-to-wire contract end-to-end.
 *
 * Throws on invalid state so the submit handler can surface the message.
 *
 * NEW_POOL mode optionally carries the governance triplet
 * ``{ ownerEmail, supervisorEmails, trigger }``. We omit each key when
 * empty / default so the wire body stays compact — the backend treats
 * absence as "use existing default" (releaser as creator, agent-derived
 * supervisor union, HYBRID trigger).
 *
 * @param {{
 *   mode: string,
 *   poolName?: string,
 *   selectedPoolIds?: string[],
 *   ownerEmail?: string,
 *   supervisorEmails?: string[],
 *   trigger?: string,
 * }} state
 * @returns {{
 *   mode: string,
 *   poolName?: string,
 *   poolIds?: string[],
 *   ownerEmail?: string,
 *   supervisorEmails?: string[],
 *   trigger?: string,
 * }}
 */
export function composeReleaseSubmitPayload(state) {
  const decision = canSubmitRelease(state);
  if (!decision.enabled) {
    throw new Error(decision.reason || 'Release form is incomplete.');
  }
  const mode = (state?.mode || '').toUpperCase();
  if (mode === RELEASE_MODE_NEW_POOL) {
    const payload = {
      mode,
      poolName: (state.poolName || '').trim(),
    };
    const ownerEmail = typeof state?.ownerEmail === 'string' ? state.ownerEmail.trim().toLowerCase() : '';
    if (ownerEmail) payload.ownerEmail = ownerEmail;

    const supervisorEmails = normalizeSupervisorEmails(state?.supervisorEmails, [ownerEmail]);
    if (supervisorEmails.length > 0) payload.supervisorEmails = supervisorEmails;

    if (state?.trigger != null && String(state.trigger).trim() !== '') {
      const trigger = normalizeTriggerMode(state.trigger);
      // Only emit when the operator picked something other than the default —
      // keeps the body tidy and lets a future schema change re-pick the
      // default without rewriting old release rows.
      if (trigger !== DEFAULT_TRIGGER_MODE) {
        payload.trigger = trigger;
      }
    }
    // Room workspace bootstrap (doc/arch/vertical/SKILLS.md) — default ON; only
    // emit ``false`` to opt out (backend treats absent as on). The backend
    // still gates on builder-present + network-skills-exist, so a true here is
    // a no-op when there's nothing to bake.
    if (state?.bootstrapWorkspace === false) {
      payload.bootstrapWorkspace = false;
    }
    // Room tool-shell seeding — mirror the network's MCP tiers into the room as
    // unconfigured shells (default ON; only emit ``false`` to opt out — backend
    // treats absent as on).
    if (state?.seedRoomTools === false) {
      payload.seedRoomTools = false;
    }
    return payload;
  }
  const patchPayload = {
    mode,
    poolIds: (state.selectedPoolIds || []).map((p) => String(p).trim()).filter(Boolean),
  };
  // Upgrade-to-existing-version: pin the patch to an already-released version
  // (e.g. ``"v2"``) so the backend reuses that release's SFN version + ARN
  // rather than cutting a new one. Only emitted on the upgrade path; a plain
  // config-only re-release omits it and the backend cuts next_version.
  const versionRef = typeof state?.versionRef === 'string' ? state.versionRef.trim() : '';
  if (state?.isUpgradeToExisting && versionRef) {
    patchPayload.versionRef = versionRef;
  }
  return patchPayload;
}
